import { motion } from "framer-motion";
import { useRef } from "react";
import { useScroll, useTransform } from "framer-motion";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import { viewport } from "../../lib/motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Cleaning done right.",
    body: "Every property gets a scope built around it — the material, the age, the level of use. Our process starts with listening. Then equipment. Then execution.",
    image: "/generated/intro_1.png",
  },
  {
    n: "02",
    title: "Trained, not casual.",
    body: "Our cleaners are trained on materials, chemistry and workflow — not just given a mop. That's what separates professional cleaning from a quick tidy.",
    image: "/generated/hero_about.png",
  },
  {
    n: "03",
    title: "Insured. Documented. Accountable.",
    body: "Public liability coverage, a clear quotation and a walk-through at completion. No surprises. No hidden charges.",
    image: "/generated/hero_whyus.png",
  },
];

export default function Manifesto() {
  return (
    <section
      data-testid="manifesto"
      className="relative bg-ink py-24 md:py-32 border-t border-charcoal/50"
    >
      <div className="container-emilio">
        <div className="mb-16 max-w-3xl">
          <Eyebrow>Manifesto</Eyebrow>
          <RevealText
            as="h2"
            text={"Three principles.\nOne standard."}
            className="mt-4 font-display font-extrabold text-white text-display"
          />
        </div>
        <div className="flex flex-col gap-24 md:gap-40">
          {CHAPTERS.map((c, i) => (
            <Chapter key={c.n} chapter={c} flipped={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Chapter({ chapter, flipped }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div
      ref={ref}
      className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
        flipped ? "lg:[direction:rtl]" : ""
      }`}
    >
      <div className="lg:col-span-5 lg:[direction:ltr] relative">
        <div className="font-display font-extrabold text-colossal text-charcoal leading-[0.85] select-none">
          {chapter.n}
        </div>
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-x-0 top-[45%] font-display font-extrabold text-white text-3xl md:text-5xl leading-none"
        >
          {chapter.title}
        </motion.h3>
      </div>
      <div className="lg:col-span-4 lg:[direction:ltr]">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 0.8 }}
          className="text-mutedtext text-base md:text-lg leading-relaxed"
        >
          {chapter.body}
        </motion.p>
      </div>
      <div className="lg:col-span-3 lg:[direction:ltr]">
        <div className="relative overflow-hidden border border-charcoal aspect-[3/4]">
          <motion.img
            src={chapter.image}
            alt=""
            style={{ y }}
            className="absolute inset-0 w-full h-full object-cover scale-110"
          />
        </div>
      </div>
    </div>
  );
}
